// breakdown-view.ts — Janus C2c: the PL BREAKDOWN panel rendered from the BROWSER's own pricing (plan
// janus-c2b-pl-is-data-plan-2026-09-16.md §3.4 / step 7). A port of the retired PHP tool options/tools/items/pl_breakdown/pl_breakdown.php
// (v3.0 ledger layout, 2026-07-02): the same markup, classes and chart canvases, so pl-charts.ts's pie/bar charts and the page CSS keep
// working — but the numbers come from calcPl's breakdown (the frozen, status-free pass), never from a server reprice.
// Pure: (view) → HTML string. The caller mounts it, runs ftMountIcons on it and initialises the charts.
import type { Row } from './read-context';

export interface PlBreakdownView {
    id: string;
    name: string;
    itemType: string;
    kind: 'item' | 'encounter_creature' | 'player_character' | 'inventory';
    /** the browser's frozen PL and its ledger */
    pl: number;
    totalGp: number;
    breakdown: Row;
    /** PL(with applied statuses) − PL(frozen); 0 when the row carries none */
    statusShare: number;
    statuses: number;
    /** what the blob holds right now (null = unpriced) and its provenance */
    stored: { value: number | null; source: 'browser-frozen' | 'server-legacy' | null; pending: boolean; current: boolean };
    baseUrl: string;
    /** the full-page view link is omitted ON the full page */
    fullPageLink: boolean;
}

const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown): number => { const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : 0; return Number.isFinite(n) ? n : 0; };
/** PHP empty(): null / undefined / false / 0 / '' / '0' / an empty array or object. */
const empty = (v: unknown): boolean => v === null || v === undefined || v === false || v === 0 || v === '' || v === '0'
    || (Array.isArray(v) && v.length === 0) || (isObj(v) && Object.keys(v).length === 0);
export function esc(v: unknown): string {
    return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}
/** PHP number_format($n, $d): half away from zero, thousands commas. */
export function fmt(n: unknown, d = 0): string {
    const x = num(n);
    const f = 10 ** d;
    const r = Math.sign(x) * Math.round(Math.abs(x) * f + 1e-9) / f;
    return r.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}
/** PHP round($n, $d) echoed as a float string (12.0 → "12"). */
const rnd = (n: unknown, d: number): string => { const f = 10 ** d; const x = num(n); return String(Math.sign(x) * Math.round(Math.abs(x) * f + 1e-9) / f); };
/** PHP round($n, $d) as a number: half away from zero (-93.75 → -93.8), with PHP's pre-rounding tolerance. */
const phpRound = (n: number, d = 0): number => { const f = 10 ** d; return Math.sign(n) * Math.round(Math.abs(n) * f + 1e-9) / f; };
const words = (s: unknown): string => String(s ?? '').replace(/_/g, ' ').replace(/\b([a-z])/g, (_, c: string) => c.toUpperCase());
const ucfirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

type PageGlobals = { FT_ICON_REGISTRY?: Record<string, unknown>; ICON_LOOKUP?: Record<string, unknown>; SKILL_CATALOG?: Record<string, unknown> };
const pageGlobals = (): PageGlobals => (typeof window !== 'undefined' ? window : {}) as PageGlobals;
/** ft_plb_icon twin: an unknown key renders nothing (a "?" in a math ledger reads like an error). Resolves like iconMap(): the front-end
 *  registry (or the legacy inline table), the trailing-s singular fallback, and the ability-type rune badges. No registry → no icon. */
function knownIcon(key: string): boolean {
    if (!key) return false;
    if (/_abilit(y|ies)$|^(spell|curse|menta|sanguine)$/.test(key)) return true;
    const g = pageGlobals();
    const reg = g.FT_ICON_REGISTRY ?? g.ICON_LOOKUP;
    if (!reg) return false;
    const has = (k: string): boolean => Object.prototype.hasOwnProperty.call(reg, k);
    return has(key) || (key.endsWith('s') && has(key.replace(/s+$/, '')));
}
/** Skill::getItemName twin: the skill catalog's display name for a variable_name, else the title-cased key. */
function skillName(key: string): string {
    const cat = pageGlobals().SKILL_CATALOG;
    if (cat) for (const entry of Object.values(cat)) {
        if (isObj(entry) && entry.variable_name === key && typeof entry.item_name === 'string' && entry.item_name !== '') return entry.item_name;
    }
    return words(key);
}
function iconPh(key: string, size = 25): string {
    if (!key) return '';
    return `<i class='ft-icon' data-icon='${esc(key)}' data-w='${size}' data-h='${size}' style='display:inline-block;width:${size}px;height:${size}px' aria-hidden='true'></i>`;
}
const plbIcon = (key: string, size = 20): string => knownIcon(key) ? iconPh(key, size) : '';

// ══════════════════════════ TRACE RENDERER (one ability_modifiers row) ══════════════════════════
function traceRow(row: Row): string {
    const out: string[] = [];
    const comp = isObj(row.base_content_details) ? row.base_content_details : {};
    const contentGp = row.content_gp ?? row.base_gp ?? 0;
    const beforeEcon = row.total_content_before_economy ?? contentGp;
    const finalGp = num(row.final_gp);
    const finalMult = row.final_multiplier ?? 1;

    const gpCell = (gp: unknown, suffix = ' gp', inner = false): string =>
        `<span class="ft-plb-num${num(gp) < 0 ? ' neg' : ''}${inner ? ' inner' : ''}">${fmt(gp)}${suffix}</span>`;
    const traceLine = (label: string, formula: string, gp: unknown, iconKey = '', sub = false, inner = false): void => {
        out.push(`<div class="ft-plb-row${sub ? ' sub' : ''}"><span class="ic">${plbIcon(iconKey, sub ? 17 : 20)}</span>`
            + `<span class="lbl"><span class="nm">${label}</span>${formula !== '' ? `<span class="frm">${esc(formula)}</span>` : ''}</span>`
            + (gp === null ? '<span></span>' : gpCell(gp, ' gp', sub || inner)) + '</div>');
    };
    const multLine = (label: string, ctx: string, mult: number, iconKey = '', sub = true): void => {
        out.push(`<div class="ft-plb-mrow${sub ? ' sub' : ''}"><span class="ic">${plbIcon(iconKey, sub ? 17 : 20)}</span>`
            + `<span class="lbl"><span class="nm">${label}</span>${ctx !== '' ? `<span class="ctx">${esc(ctx)}</span>` : ''}</span>`
            + `<span class="mval ${mult < 1 ? 'down' : 'up'}">× ${fmt(mult, 2)}</span></div>`);
    };
    const subsumLine = (label: string, gp: unknown, note = ''): void => {
        out.push(`<div class="ft-plb-subsum"><span>${label}${note !== '' ? ` <span class="ctx">${esc(note)}</span>` : ''}</span>${gpCell(gp)}</div>`);
    };

    out.push('<div class="ft-plb-sec">');
    out.push('<div class="ft-plb-sec-title">1 · Content <span class="sub">— what the effect is made of</span></div>');
    let contentRows = 0;

    if (!empty(comp.damage_roll)) {
        const d = isObj(comp.damage_roll_details) ? comp.damage_roll_details : {};
        if (!empty(d)) {
            const baseGp = phpRound(num(d.avg_damage) * num(d.gp_per_avg_point));
            const hasSubs = !empty(d.push_gp) || (!empty(d.type_multiplier) && num(d.type_multiplier) !== 1)
                || !empty(d.natural_premium) || !empty(d.extra_ap_penalty) || !empty(d.at_will_multiplier);
            traceLine('Damage', `${d.dice ?? '?'} · avg ${d.avg_damage ?? '?'} × ${d.gp_per_avg_point ?? '?'} gp/pt`, baseGp, 'damage_roll', false, hasSubs);
            if (!empty(d.push_gp)) traceLine(`Push ${d.push_ft} ft`, `${d.push_ft} ft × 20 gp/ft`, d.push_gp, 'push', true);
            if (!empty(d.type_multiplier) && num(d.type_multiplier) !== 1) multLine(`${ucfirst(String(d.damage_type ?? 'typed'))} damage`, 'damage-type premium', num(d.type_multiplier), String(d.damage_type ?? ''));
            if (!empty(d.natural_premium)) multLine('Natural weapon', 'cannot be disarmed', num(d.natural_premium), 'unhindered_attack');
            if (!empty(d.extra_ap_penalty)) multLine('Extra AP cost', '', num(d.extra_ap_penalty), 'ap');
            if (!empty(d.at_will_multiplier)) multLine('At-will', 'usable every turn', num(d.at_will_multiplier), 'at_will_spell');
            if (hasSubs) subsumLine('Damage subtotal', comp.damage_roll);
        } else {
            traceLine('Damage', '', comp.damage_roll, 'damage_roll');
        }
        contentRows++;
    }

    for (const sa of Array.isArray(comp.status_applications) ? comp.status_applications as Row[] : Object.values(isObj(comp.status_applications) ? comp.status_applications : {}) as Row[]) {
        if (!isObj(sa)) continue;
        const der = isObj(sa.derivation) ? sa.derivation : {};
        const layerStr = String(sa.layer ?? '');
        const isRemoval = layerStr.includes('removed_from');
        const who = layerStr.replace(/status_effect_applied_to_|status_effect_removed_from_|after_contesting_roll_failed|on_hit/g, '').replace(/_/g, ' ').trim();
        const when = (isRemoval ? 'removes from ' : 'applies to ') + (who !== '' ? who : 'target');
        const neg = num(sa.gp) < 0;
        const statusIcon = String(sa.var ?? 'status_effect');
        const statusLbl = 'Status: ' + esc(sa.name ?? '?');
        if (!empty(der)) {
            const rate = der.application_rate ?? 0.4;
            const base = phpRound(num(der.immunity_gp) * num(rate));
            const hasSubs = (der.reliability !== undefined && der.reliability !== null && num(der.reliability) !== 1)
                || (der.self_discount !== undefined && der.self_discount !== null) || (der.duration_multiplier !== undefined && der.duration_multiplier !== null);
            traceLine(statusLbl, `${when} · immunity ${fmt(der.immunity_gp ?? 0)} gp × ${phpRound(num(rate) * 100)}% application`, base, statusIcon, false, hasSubs || neg);
            if (der.reliability !== undefined && der.reliability !== null && num(der.reliability) !== 1) multLine('Contest-gated', 'only lands on a failed contesting roll', num(der.reliability), 'contesting_roll');
            if (der.self_discount !== undefined && der.self_discount !== null) multLine('Self-cast', 'caster buffs themselves', num(der.self_discount), 'caster');
            if (der.duration_multiplier !== undefined && der.duration_multiplier !== null) multLine('Duration', String(der.duration_label ?? ''), num(der.duration_multiplier), 'duration');
            if (hasSubs || neg) subsumLine(`${esc(sa.name ?? 'Status')} subtotal`, sa.gp ?? 0, neg ? 'self-drawback, subtracts' : '');
        } else {
            traceLine(statusLbl, when + (neg ? ' — self-drawback, subtracts' : ''), sa.gp ?? 0, statusIcon);
        }
        contentRows++;
    }

    for (const [field, m] of Object.entries(isObj(comp.misc_modifiers) ? comp.misc_modifiers : {})) {
        const mm = isObj(m) ? m : {};
        traceLine(esc(mm.note ?? words(field)), '', mm.gp ?? 0, field);
        contentRows++;
    }

    const grantLabels: Record<string, string> = {
        roll_type_adv: 'ADV on %s', roll_type_dis: 'DIS on %s', roll_type_against_adv: '%s against you at ADV', roll_type_against_dis: '%s against you at DIS',
        roll_type_is_rolled_at_min: '%s rolled at minimum', roll_type_is_rolled_at_max: '%s rolled at maximum', roll_type_roll_result_is_halved: '%s result halved',
        roll_type_roll_result_is_doubled: '%s result doubled', skill_roll_adv: 'ADV on %s rolls', skill_roll_dis: 'DIS on %s rolls', skill_roll_mod_plus1: '+1 on %s rolls',
        skill_roll_mod_minus1: '−1 on %s rolls', skill_is_rolled_at_min: '%s rolled at minimum', skill_is_rolled_at_max: '%s rolled at maximum',
        all_skill_rolls_adv: 'ADV on all skill rolls', all_skill_rolls_dis: 'DIS on all skill rolls', all_skill_rolls_plus1: '+1 on all skill rolls',
        all_skill_rolls_minus1: '−1 on all skill rolls', ascension_skill_point_range_mod_minus1: 'Ascension skill point range −1',
    };
    for (const g of listOf(comp.roll_modifier_grants)) {
        const rollVar = String(g.roll_type ?? '?');
        const rollPretty = rollVar === 'attack_rolls_any' ? 'Attack Rolls (any type)' : words(rollVar);
        const lbl = (grantLabels[String(g.field ?? '')] ?? '%s').replace('%s', rollPretty);
        const der = isObj(g.derivation) ? g.derivation : {};
        let formula = '';
        if (!empty(der)) {
            formula = `permanent grant ${fmt(der.base_gp ?? 0)} gp`;
            if (der.cluster_count !== undefined && der.cluster_count !== null) formula += ` (strongest of ${der.cluster_count} alternative attack-roll types) × ${der.cluster_multiplier}`;
            const uptime = der.uptime ?? 0.4;
            formula += ` × ${phpRound(num(uptime) * 100)}% uptime${num(uptime) <= 0.1 ? ' (next roll only)' : ''}`;
            if (der.duration_multiplier !== undefined && der.duration_multiplier !== null && num(der.duration_multiplier) !== 1) {
                formula += ` × ${der.duration_multiplier} duration${!empty(der.duration_label) ? ` (${der.duration_label})` : ''}`;
            }
        }
        traceLine(esc(lbl), formula, g.gp ?? 0, rollVar === 'attack_rolls_any' ? 'roll_type' : rollVar);
        contentRows++;
    }

    const dmgdefLabels: Record<string, string> = {
        damage_type_resistance: 'Resistance: %s', damage_type_immunity: 'Immunity: %s', damage_type_vulnerability: 'Vulnerability: %s',
        ignore_damage_type_resistance: 'Ignores resistance: %s', spell_damage_resistance: 'Spell damage resistance', immune_to_spells: 'Immune to spells',
        force_move_resistance: 'Resistance to forced movement (contest ADV)', immune_to_forced_movement: 'Immune to forced movement', immune_to_disarm: 'Immune to disarm',
        all_damage_output_changed_by_percentage: 'All damage output %+d%%', all_incoming_damage_changed_by_percentage: 'All incoming damage %+d%%',
        negate_falling_damage_in_feet: 'Negate falling damage (%d ft)', ac_mod: 'Armor Class %+d', ac_mod_magical: 'Magical Armor Class %+d',
        ac_mod_non_magical: 'Non-magical Armor Class %+d', damage_mitigation: 'Damage Mitigation %+d',
    };
    for (const g of listOf(comp.damage_defense_grants)) {
        const field = String(g.field ?? '');
        const tpl = dmgdefLabels[field] ?? words(g.field ?? '?');
        let lbl: string;
        if (tpl.includes('%s')) lbl = tpl.replace('%s', words(g.damage_type ?? '?'));
        else if (tpl.includes('%')) { const v = Math.trunc(num(g.value)); lbl = tpl.replace('%+d', (v >= 0 ? '+' : '') + v).replace('%d', String(v)).replace('%%', '%'); }
        else lbl = tpl;
        const der = isObj(g.derivation) ? g.derivation : {};
        let formula = '';
        if (!empty(der)) {
            formula = `permanent grant ${fmt(der.base_gp ?? 0)} gp`;
            if (der.duration_multiplier !== undefined && der.duration_multiplier !== null && num(der.duration_multiplier) !== 1) {
                formula += ` × ${der.duration_multiplier} duration${!empty(der.duration_label) ? ` (${der.duration_label})` : ''}`;
            }
        }
        const icon = String(g.damage_type ?? '') !== '' ? String(g.damage_type) : field;
        traceLine(esc(lbl), formula, g.gp ?? 0, icon);
        contentRows++;
    }

    if (!empty(comp.granted_categories) && isObj(comp.granted_categories)) {
        const gc = comp.granted_categories;
        const der = isObj(gc.derivation) ? gc.derivation : {};
        traceLine('Granted while active', `permanent value ${fmt(der.base_gp ?? 0)} gp × duration`, null, 'duration');
        const emitLeaves = (node: unknown, path: string[]): void => {
            if (!isObj(node) && !Array.isArray(node)) return;
            const n = node as Row;
            if (n.gp !== undefined && n.gp !== null && Number.isFinite(Number(n.gp)) && String(n.gp).trim() !== '') {
                traceLine(esc(words(path.slice(-2).join(' · '))), '', n.gp, path[path.length - 1] ?? '', true);
                return;
            }
            for (const [k, child] of Object.entries(n)) emitLeaves(child, [...path, k]);
        };
        emitLeaves(gc.details ?? {}, []);
        if (der.duration_multiplier !== undefined && der.duration_multiplier !== null && num(der.duration_multiplier) !== 1) multLine('Duration', String(der.duration_label ?? ''), num(der.duration_multiplier), 'duration');
        subsumLine('Granted subtotal', gc.gp ?? 0);
        contentRows++;
    }

    for (const [k, lbl, ic] of [['force_movement', 'Forced movement', 'forced_move'], ['skill_roll_modifiers', 'Skill roll modifiers', 'skill_roll_mod'], ['roll_debuffs', 'Roll debuffs', 'roll_dis'], ['aoe', 'AOE (content)', 'aoe']] as const) {
        const v = comp[k];
        if (!empty(v) && (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))))) { traceLine(lbl, '', v, ic); contentRows++; }
    }
    if (contentRows === 0) traceLine('Base cost', row.base_gp !== undefined && row.base_gp !== null ? 'spell-level base cost table' : 'no priced content layers', contentGp, 'cast_spell');
    out.push(`<div class="ft-plb-sum"><span>Content total</span>${gpCell(contentGp)}</div>`);

    let preShown = false;
    for (const [k, lbl, ic] of [['tier_discount', 'Skill-tier requirement discount', 'skill_tier'], ['weapon_attached_discount', 'Attached-to-weapon discount', 'weapon'], ['slot_penalty', 'Slot penalty', 'equipment']] as const) {
        const cell = row[k];
        const mult = isObj(cell) && cell.multiplier !== undefined && cell.multiplier !== null ? num(cell.multiplier) : 1;
        if (Math.abs(mult - 1) >= 0.01) {
            out.push(`<div class="ft-plb-mrow"><span class="ic">${plbIcon(ic)}</span><span class="lbl"><span class="nm">${lbl}</span></span><span class="mval ${mult < 1 ? 'down' : 'up'}">× ${mult}</span></div>`);
            preShown = true;
        }
    }
    if (preShown && num(beforeEcon) !== num(contentGp)) out.push(`<div class="ft-plb-sum"><span>Before multipliers</span>${gpCell(beforeEcon)}</div>`);

    const econ = isObj(row.economy_details) ? row.economy_details : {};
    if (!empty(econ)) {
        const stepMeta: Record<string, [string, string]> = {
            ap_economy: ['Action Points', 'ap'], range: ['Range', 'range'], recharge: ['Recharge', 'rechargeable'],
            weapon_classification_restriction: ['Weapon Restriction', 'weapon_classification'], triggers: ['Triggered', 'roll_triggers'],
            roll_type: ['Roll Type', 'roll_type'], size_restriction: ['Size Restriction', 'size'], ignore_resistance: ['Ignores Resistance', 'damage_type_resistance'],
            aoe: ['Area of Effect', 'aoe'], charge_gating: ['Uses Charges', 'ability_charges'], concentration: ['Concentration', 'concentration'],
            casting_time: ['Casting Time', 'cast_spell'], conditional_gate: ['Conditional Gate', 'parameters'],
            target_restriction: ['Target Restriction', 'ability_works_only_against_creature_type'], upcastable: ['Upcastable', 'spell_upcastable'],
            contest_retry: ['Contest Retry', 'skill_roll_retracting_roll'],
            contest_retry_on_damage: ['Contest Retry on Damage', 'target_retries_contesting_roll_on_damage_taken'],
            status_caster_choice: ['Caster Chooses One Status', 'status_effect_applied_is_caster_choice'], save_for_half: ['Contesting Roll for Half', 'contesting_roll_successful_for_half_damage'],
            contest_skip_same_team: ['Willing Allies Skip Contest', 'contesting_roll_skipped_for_same_team_type'], granted_for_duration: ['Granted For Duration', 'duration'],
        };
        out.push('<div class="ft-plb-sec-title econ">2 · Multipliers <span class="sub">— how usable / reliable it is</span></div>');
        let stepsProduct = 1;
        let shown = 0;
        for (const [step, info] of Object.entries(econ)) {
            const inf = isObj(info) ? info : null;
            const mult = inf && inf.multiplier !== undefined && inf.multiplier !== null ? num(inf.multiplier) : 1;
            stepsProduct *= mult;
            if (Math.abs(mult - 1) < 0.005) continue;
            let ctx = '';
            if (inf) {
                for (const ck of ['value', 'ft', 'type', 'note', 'count']) {
                    const v = inf[ck];
                    if (v !== undefined && v !== null && typeof v !== 'object' && String(v) !== '' && String(v) !== '0' && v !== false) { ctx = v === true ? '1' : String(v); break; }
                }
                if (step === 'recharge' && inf.begin !== undefined && inf.begin !== null && inf.end !== undefined && inf.end !== null) ctx = `recharges on ${inf.begin}-${inf.end}`;
                if (step === 'aoe' && inf.ft !== undefined && inf.ft !== null) {
                    ctx = `${inf.ft} ft${!empty(inf.shape) ? ' ' + inf.shape : ''}`;
                    if (!empty(inf.area_sqft)) ctx += ` · ${fmt(inf.area_sqft)} sq ft board`;
                    if (!empty(inf.volume_cuft)) ctx += ` · ${fmt(inf.volume_cuft)} cu ft 3d`;
                }
            }
            let [lbl, ic] = stepMeta[step] ?? [words(step), step];
            const value = inf ? String(inf.value ?? '') : '';
            let specific = '';
            if (step === 'range') specific = 'range_' + value;
            else if (step === 'roll_type') specific = value;
            else if (step === 'aoe') specific = inf ? String(inf.shape ?? '') : '';
            else if (step === 'casting_time') specific = ['1ap', '2ap', '3ap', 'reaction'].includes(value) ? value : (value !== '' ? 'casting_ritual' : '');
            else if (step === 'ap_economy') specific = inf ? String(inf.type ?? '') : '';
            if (specific !== '' && plbIcon(specific) !== '') ic = specific;
            out.push(`<div class="ft-plb-mrow"><span class="ic">${plbIcon(ic)}</span><span class="lbl"><span class="nm">${lbl}</span>`
                + `${ctx !== '' ? `<span class="ctx">${esc(ctx.replace(/_/g, ' '))}</span>` : ''}</span><span class="mval ${mult < 1 ? 'down' : 'up'}">× ${fmt(mult, 2)}</span></div>`);
            shown++;
        }
        if (shown === 0) out.push('<div class="ft-plb-mrow"><span class="ic"></span><span class="lbl"><span class="ctx">no adjustments — every multiplier is ×1.00</span></span><span class="mval">× 1.00</span></div>');
        const raw3 = phpRound(stepsProduct, 3);
        const floored = raw3 < num(finalMult) - 0.004;
        out.push(`<div class="ft-plb-sum"><span>Combined multiplier${floored ? ` <span class="badge bg-warning text-dark ms-1">raw ${raw3} floored to ${finalMult}</span>` : ''}</span><span class="ft-plb-num">× ${finalMult}</span></div>`);
    }
    out.push('</div>');
    out.push(`<div class="ft-plb-final"><span class="eq">${fmt(beforeEcon)} gp × ${finalMult}</span><span class="res">${fmt(finalGp)} gp&ensp;=&ensp;${fmt(finalGp / 100, 2)} PL</span></div>`);
    out.push('<div class="ft-plb-legend">PL = gp ÷ 100</div>');
    return out.join('');
}

function listOf(v: unknown): Row[] {
    if (Array.isArray(v)) return v.filter(isObj);
    if (isObj(v)) return Object.values(v).filter(isObj);
    return [];
}

/** ft_plb_cat_gp twin: the recursive sum of every `gp` leaf. */
export function categoryGp(items: unknown): number {
    let gp = 0;
    const walk = (node: unknown): void => {
        if (Array.isArray(node)) { for (const c of node) walk(c); return; }
        if (!isObj(node)) return;
        for (const [k, v] of Object.entries(node)) {
            if (k === 'gp' && (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))))) gp += Number(v);
            else if (v !== null && typeof v === 'object') walk(v);
        }
    };
    walk(items);
    return gp;
}

export interface AbilityRow { type: string; uuid: string; name: string; row: Row }
export function abilityRows(breakdown: Row): AbilityRow[] {
    const out: AbilityRow[] = [];
    const mods = isObj(breakdown.ability_modifiers) ? breakdown.ability_modifiers : {};
    for (const [type, typeRows] of Object.entries(mods)) {
        for (const [uuid, row] of Object.entries(isObj(typeRows) ? typeRows : Array.isArray(typeRows) ? Object.fromEntries(typeRows.map((r, i) => [String(i), r])) : {})) {
            if (!isObj(row)) continue;
            out.push({ type, uuid, name: String(row.ability_name ?? row.spell_name ?? 'Ability'), row });
        }
    }
    return out;
}

/** The top category by |gp| — the review pages' "top driver" (the same walk the PHP pages did). */
export function topDriver(breakdown: Row): string {
    let topCat = ''; let topGp = 0;
    for (const [cat, entries] of Object.entries(breakdown)) {
        const gp = categoryGp(entries);
        if (Math.abs(gp) > Math.abs(topGp)) { topGp = gp; topCat = cat; }
    }
    return topCat.replace(/_/g, ' ');
}

const FRIENDLY: Record<string, string> = {
    essences: 'Essences', senses: 'Senses', speed_types: 'Speed Types', skill_tiers: 'Skill Tiers', misc_modifiers: 'Miscellaneous',
    vitality: 'Vitality (V)', prepared_spells: 'Prepared Spells', armor_modifiers: 'Armor & Defenses',
};
const COLORS: Record<string, string> = {
    Essences: '#28a745', Senses: '#17a2b8', 'Speed Types': '#0d6efd', 'Skill Tiers': '#6f42c1', Miscellaneous: '#ffc107', 'Vitality (V)': '#fd7e14',
    'Prepared Spells': '#20c997', Abilities: '#dc3545', 'Status Effects': '#adb5bd', 'Armor & Defenses': '#6f42c1',
};
const PURE_ABILITY_TYPES = new Set(['reaction_ability', 'quick_ability', 'free_ability', 'passive_ability', 'opportunity_attack_ability', 'full_ability', 'attack_ability', 'ability', 'curse', 'menta', 'sanguine', 'spell']);

export function plPieData(breakdown: Row): { labels: string[]; values: number[]; colors: string[] } {
    const labels: string[] = []; const values: number[] = []; const colors: string[] = [];
    for (const [category, items] of Object.entries(breakdown)) {
        if (empty(items) || category === 'ability_modifiers') continue;
        const label = FRIENDLY[category] ?? ucfirst(category.replace(/_/g, ' '));
        const catPl = phpRound(categoryGp(items) / 100, 1);
        if (catPl !== 0) { labels.push(label); values.push(Math.abs(catPl)); colors.push(COLORS[label] ?? '#6c757d'); }
    }
    const rows = abilityRows(breakdown);
    if (rows.length) {
        const ab = rows.reduce((a, r) => a + num(r.row.final_gp), 0);
        if (ab !== 0) { labels.push('Abilities'); values.push(Math.abs(phpRound(ab / 100, 1))); colors.push(COLORS.Abilities!); }
    }
    return { labels, values, colors };
}

const STYLE = `<style>
.ft-plb-card{border:1px solid rgba(128,148,180,.22);border-radius:.75rem;overflow:hidden;background:rgba(128,148,180,.05);box-shadow:0 1px 10px rgba(0,0,0,.18);margin-bottom:1rem}
.ft-plb-card-head{display:flex;justify-content:space-between;align-items:center;gap:.5rem;padding:.6rem .9rem;background:linear-gradient(90deg,rgba(13,110,253,.16),transparent 70%);border-bottom:1px solid rgba(128,148,180,.22)}
.ft-plb-card-head .ttl{font-weight:600}
.ft-plb-card-head .typ{opacity:.55;font-size:.75rem;text-transform:uppercase;letter-spacing:.05em}
.ft-plb-pl-badge{font-variant-numeric:tabular-nums;background:#0d6efd;color:#fff;border-radius:2rem;padding:.15rem .65rem;font-weight:700;white-space:nowrap}
.ft-plb-sec{padding:.35rem .9rem .5rem;text-align:left}
.ft-plb-sec-title{font-size:.7rem;letter-spacing:.09em;text-transform:uppercase;opacity:.75;font-weight:700;margin:.7rem 0 .25rem;display:flex;align-items:center;gap:.45rem}
.ft-plb-sec-title .sub{opacity:.6;font-weight:600;letter-spacing:.05em}
.ft-plb-sec-title::before{content:"";display:inline-block;width:.55rem;height:.55rem;border-radius:.15rem;background:#0d6efd;flex:none}
.ft-plb-sec-title.econ::before{background:#20c997}
.ft-plb-row,.ft-plb-mrow{display:grid;grid-template-columns:22px minmax(0,1fr) auto;gap:.15rem .55rem;align-items:center;padding:.34rem .15rem;border-bottom:1px solid rgba(128,148,180,.13);font-size:.85rem;text-align:left}
.ft-plb-row:last-of-type,.ft-plb-mrow:last-of-type{border-bottom:0}
.ft-plb-mrow{transition:background .12s}
.ft-plb-mrow:hover,.ft-plb-row:hover{background:rgba(128,148,180,.07)}
.ft-plb-row .ic,.ft-plb-mrow .ic{width:22px;min-height:1px;display:flex;align-items:center;justify-content:center;opacity:.92}
.ft-plb-row .lbl,.ft-plb-mrow .lbl{min-width:0}
.ft-plb-row .nm,.ft-plb-mrow .nm{font-weight:600}
.ft-plb-row .frm{opacity:.55;font-size:.74rem;line-height:1.35;font-family:var(--bs-font-monospace,SFMono-Regular,Menlo,Consolas,monospace);margin-left:.5rem}
.ft-plb-mrow .ctx,.ft-plb-subsum .ctx{opacity:.55;font-size:.74rem;line-height:1.3;margin-left:.5rem;font-weight:400}
.ft-plb-num.inner,.ft-plb-mrow.sub .mval{margin-right:1.75rem}
.ft-plb-row.sub,.ft-plb-mrow.sub{padding-left:1.5rem;font-size:.8rem;border-bottom:0}
.ft-plb-row.sub .nm,.ft-plb-mrow.sub .nm{font-weight:500;opacity:.85}
.ft-plb-subsum{display:flex;justify-content:space-between;align-items:center;gap:.55rem;padding:.28rem .15rem .3rem 1.5rem;margin-bottom:.15rem;font-weight:600;font-size:.8rem;border-bottom:1px solid rgba(128,148,180,.13);text-align:left}
.ft-plb-subsum .ft-plb-num{border-top:1px dashed rgba(128,148,180,.45);padding-top:.15rem}
.ft-plb-num{font-variant-numeric:tabular-nums;white-space:nowrap;text-align:right;min-width:5.5rem;font-family:var(--bs-font-monospace,SFMono-Regular,Menlo,Consolas,monospace);font-size:.82rem;justify-self:end}
.ft-plb-num.neg{color:#e4737f}
.ft-plb-mrow .mval{font-variant-numeric:tabular-nums;white-space:nowrap;text-align:right;min-width:5.5rem;justify-self:end;font-family:var(--bs-font-monospace,SFMono-Regular,Menlo,Consolas,monospace);font-size:.82rem;font-weight:700}
.ft-plb-mrow .mval.up{color:#4fc396}
.ft-plb-mrow .mval.down{color:#e4737f}
.ft-plb-sum{display:flex;justify-content:space-between;align-items:center;padding:.42rem .15rem .15rem;font-weight:700;border-top:2px solid rgba(128,148,180,.35);margin-top:.2rem;font-size:.87rem}
.ft-plb-final{display:flex;justify-content:space-between;align-items:center;gap:.75rem;margin:.55rem .9rem .3rem;padding:.6rem .2rem .2rem;border-top:3px double rgba(128,148,180,.55);color:inherit}
.ft-plb-final .eq{opacity:.65;font-size:.82rem;font-variant-numeric:tabular-nums;font-family:var(--bs-font-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.ft-plb-final .res{font-size:1.02rem;font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap;font-family:var(--bs-font-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.ft-plb-legend{text-align:right;font-size:.68rem;opacity:.45;margin:0 .95rem .55rem}
details.ft-plb-det{border:1px solid rgba(128,148,180,.22);border-radius:.55rem;margin-bottom:.4rem;background:rgba(128,148,180,.04);transition:background .15s}
details.ft-plb-det:hover{background:rgba(128,148,180,.09)}
details.ft-plb-det>summary{display:flex;justify-content:space-between;align-items:center;gap:.5rem;padding:.5rem .75rem;cursor:pointer;list-style:none}
details.ft-plb-det>summary::-webkit-details-marker{display:none}
details.ft-plb-det>summary::before{content:"▸";opacity:.5;margin-right:.35rem;transition:transform .15s;flex:none}
details.ft-plb-det[open]>summary::before{transform:rotate(90deg)}
details.ft-plb-det[open]>summary{border-bottom:1px solid rgba(128,148,180,.22)}
details.ft-plb-det>summary .nm{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ft-plb-header{display:flex;justify-content:space-between;align-items:center;gap:.75rem;margin-bottom:.4rem;padding-bottom:.6rem;border-bottom:2px solid rgba(13,110,253,.45)}
.ft-plb-header h5{margin:0;display:flex;align-items:center;gap:.6rem;min-width:0}
.ft-plb-header .nm{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ft-plb-prov{font-size:.74rem;opacity:.7;margin-bottom:.9rem;text-align:left}
.ft-plb-prov .warn{color:#e2b93d}
</style>`;

const li = (left: string, right: string): string =>
    `<li class="d-flex justify-content-between align-items-center py-2 px-4 border-bottom" style="margin-left:14px; margin-right:14px;">${left}<span class="badge bg-light text-dark">${right}</span></li>`;

/** The provenance line under the header: what this panel priced, and how it relates to the stored number. */
function provenance(v: PlBreakdownView): string {
    const parts: string[] = [];
    if (v.statuses > 0) parts.push(`${v.statuses} applied status effect${v.statuses === 1 ? '' : 's'} excluded (${v.statusShare >= 0 ? '+' : ''}${fmt(v.statusShare, 2)} PL while applied)`);
    const s = v.stored;
    if (s.value === null) parts.push(s.pending ? 'stored value pending' : 'no stored value yet');
    else if (Math.abs(s.value - v.pl) > 0.005) parts.push(`<span class="warn">stored ${fmt(s.value, 2)} PL${s.source === 'server-legacy' ? ' (legacy server price)' : ''} — refreshes on the next heal</span>`);
    else if (s.source === 'server-legacy') parts.push('matches the stored legacy price');
    return parts.length ? `<div class="ft-plb-prov">${parts.join(' · ')}</div>` : '';
}

export function renderPlBreakdownHtml(v: PlBreakdownView): string {
    const breakdown = v.breakdown;
    const out: string[] = [STYLE];
    const itemUrl = `${v.baseUrl}item.php?id=${encodeURIComponent(v.id)}`;
    out.push(`<div class="ft-plb-header"><h5><span class="ft-plb-pl-badge">${fmt(v.pl, 2)} PL</span>`
        + `<a class="nm" href="${esc(itemUrl)}" target="_blank" style="color:inherit;text-decoration:underline dotted rgba(128,148,180,.6);text-underline-offset:3px">${esc(v.name || 'Unknown Item')}</a></h5></div>`);
    out.push(provenance(v));

    const pie = plPieData(breakdown);
    out.push(`<div class="mb-3 d-flex justify-content-end gap-2"><button onclick="switchPlChart('pie')" id="btn-pie" class="btn btn-sm btn-primary active">${iconPh('pie_chart')}</button>`
        + `<button onclick="switchPlChart('bar')" id="btn-bar" class="btn btn-sm btn-outline-primary">${iconPh('bar_chart')}</button></div>`);
    out.push(`<div class="mb-4 d-flex justify-content-center position-relative" style="height: 195px;"><div style="max-width: 215px; width: 100%;">`
        + `<canvas id="plPieChart" height="195" style="display:block !important;" data-labels="${esc(JSON.stringify(pie.labels))}" data-values="${esc(JSON.stringify(pie.values))}" data-colors="${esc(JSON.stringify(pie.colors))}"></canvas>`
        + `</div><canvas id="plBarChart" height="195" style="display:none; width:100%;"></canvas></div>`);

    const isPureAbility = PURE_ABILITY_TYPES.has(v.itemType);
    // pl_breakdown.php's $is_encompassed_item: any root that is not a creature, encounter creature or player character (an inventory instance too)
    const isEncompassed = v.kind !== 'encounter_creature' && v.kind !== 'player_character' && v.itemType !== 'creature';
    const rows = abilityRows(breakdown);
    if (isPureAbility && rows.length) {
        out.push('<div class="mb-3">');
        for (const r of rows) {
            out.push(`<div class="ft-plb-card"><div class="ft-plb-card-head"><span class="ttl">${plbIcon(r.type)} ${esc(r.name)} <span class="typ">${esc(r.type.replace(/_/g, ' '))}</span></span>`
                + `<span class="ft-plb-pl-badge">${fmt(num(r.row.final_gp) / 100, 2)} PL</span></div>${traceRow(r.row)}</div>`);
        }
        out.push('</div>');
    }

    out.push('<div class="accordion" id="plAccordion">');
    for (const [category, items] of Object.entries(breakdown)) {
        if (empty(items) || category === 'ability_modifiers' || category === 'status_effect_modifiers') continue;
        const label = FRIENDLY[category] ?? words(category);
        const iconKey = category === 'misc_modifiers' ? 'miscellaneous' : category;
        const catTotal = phpRound(categoryGp(items) / 100, 1);
        if (catTotal === 0 && category !== 'speed_types') continue;
        const body: string[] = ['<ul class="list-unstyled mb-0">'];
        const it = isObj(items) ? items : {};
        if (category === 'vitality') {
            body.push(li(`<span class="text-muted small d-flex align-items-center">${iconPh('vitality')} V Max <span class="ms-3 small opacity-75">${esc(it.v_max)} V × ${esc(it.gp_per_point)} GP/V</span></span>`, `${esc(it.pl)} PL`));
        } else if (category === 'prepared_spells') {
            const sorted = Object.values(it).filter(isObj).sort((a, b) => num(a.spell_level) - num(b.spell_level));
            for (const d of sorted) {
                if (d.gp === undefined || d.gp === null) continue;
                body.push(li(`<span class="d-flex align-items-center"><span class="badge bg-primary me-2">${esc(d.spell_level)}</span>${esc(d.spell_name)}</span>`, `${rnd(num(d.gp) / 100, 2)} PL`));
            }
        } else if (category === 'speed_types' || category === 'senses') {
            for (const [key, d] of Object.entries(it)) {
                if (!isObj(d) || d.raw_value === undefined || d.raw_value === null) continue;
                body.push(li(`<span class="text-muted small d-flex align-items-center"><span class="pe-2">${iconPh(key)}</span> ${esc(ucfirst(key.replace(/^speed_/, '').replace(/_/g, ' ')))} <span class="ms-3 small opacity-75">${esc(d.raw_value)} ft × ${esc(d.gp_per_unit ?? '?')} gp / 5 ft</span></span>`, `${rnd(num(d.gp) / 100, 2)} PL`));
            }
        } else if (category === 'armor_modifiers') {
            const armor: Record<string, [string, string]> = { ac_mod: ['armor', 'Base Armor Class'], damage_mitigation: ['shield', 'Damage Mitigation (DR)'], ac_mod_magical: ['magic', 'Magical Armor Class'] };
            for (const [key, d] of Object.entries(it)) {
                const gp = isObj(d) ? num(d.gp) : 0;
                if (gp === 0 || !isObj(d)) continue;
                const [ic, lbl] = armor[key] ?? ['', words(key)];
                body.push(li(`<span class="text-muted small d-flex align-items-center"><span class="pe-2">${ic ? iconPh(ic) : ''}</span>${esc(lbl)}${d.value !== undefined && d.value !== null ? ` <span class="ms-3 small opacity-75">+${esc(d.value)}</span>` : ''}</span>`, `${rnd(gp / 100, 2)} PL`));
            }
        } else {
            const flat: Array<[string, Row, string | null, string | null]> = [];
            for (const [key, d] of Object.entries(it)) {
                if (isObj(d) && (d.gp === undefined || d.gp === null)) {
                    for (const [sub, subrow] of Object.entries(d)) if (isObj(subrow) && subrow.gp !== undefined && subrow.gp !== null) flat.push([key, subrow, `${words(key)} · ${words(sub)}`, sub]);
                } else if (isObj(d)) flat.push([key, d, null, null]);
            }
            for (const [key, d, nestedLabel, nestedIcon] of flat) {
                const gp = num(d.gp); if (gp === 0) continue;
                let extra = '';
                if (category === 'essences' && d.raw_value !== undefined && d.raw_value !== null) extra = ` <span class="ms-3 small opacity-75">${esc(d.raw_value)} × ${fmt(d.gp_per_unit ?? 0)} gp</span>`;
                if (category === 'skill_tiers' && d.tier !== undefined && d.tier !== null) extra = ` <span class="ms-3 small opacity-75">${esc(String(d.tier).replace(/_/g, ' '))}</span>`;
                body.push(li(`<span class="text-muted small d-flex align-items-center"><span class="pe-2">${iconPh((nestedIcon ?? key).toLowerCase())}</span>${esc(nestedLabel ?? skillName(key))}${extra}</span>`, `${rnd(gp / 100, 2)} PL`));
            }
        }
        body.push('</ul>');
        out.push(`<div class="accordion-item"><h2 class="accordion-header"><button class="accordion-button collapsed d-flex align-items-center" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-${esc(category)}">`
            + `${iconPh(iconKey)}&nbsp; ${esc(label)}<span class="ms-2 badge bg-secondary">${catTotal} PL</span></button></h2>`
            + `<div id="collapse-${esc(category)}" class="accordion-collapse collapse"><div class="accordion-body p-0">${body.join('')}</div></div></div>`);
    }

    if (!isPureAbility && rows.length) {
        const abTotal = rows.reduce((a, r) => a + num(r.row.final_gp) / 100, 0);
        const sorted = [...rows].sort((a, b) => num(b.row.final_gp) - num(a.row.final_gp));
        out.push(`<div class="accordion-item"><h2 class="accordion-header"><button class="accordion-button collapsed d-flex align-items-center" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-ability-rows">`
            + `${iconPh('passive_ability')}&nbsp; Abilities &amp; Spells<span class="ms-2 badge bg-secondary">${rnd(abTotal, 1)} PL</span><span class="ms-2 badge bg-light text-dark">${rows.length}</span></button></h2>`
            + '<div id="collapse-ability-rows" class="accordion-collapse collapse"><div class="accordion-body p-2">');
        for (const r of sorted) {
            out.push(`<details class="ft-plb-det"><summary><span class="nm small">${plbIcon(r.type, 18)} ${esc(r.name)} <span class="text-muted">(${esc(r.type.replace(/_/g, ' '))})</span></span>`
                + `<span class="ft-plb-pl-badge">${fmt(num(r.row.final_gp) / 100, 2)} PL</span></summary>${traceRow(r.row)}</details>`);
        }
        out.push('</div></div></div>');
    }

    const sem = isObj(breakdown.status_effect_modifiers) ? breakdown.status_effect_modifiers : {};
    for (const [layerType, entries] of Object.entries(sem)) {
        const list = isObj(entries) ? entries : {};
        const ltGp = Object.values(list).reduce((a: number, e) => a + (isObj(e) ? num(e.gp) : 0), 0);
        if (ltGp === 0) continue;
        const lis: string[] = [];
        for (const [varName, d] of Object.entries(list)) {
            const gp = isObj(d) ? num(d.gp) : 0; if (gp === 0) continue;
            lis.push(`<li class="d-flex justify-content-between align-items-center py-2 px-4 border-bottom" style="margin-left:14px; margin-right:14px;"><span class="text-muted small d-flex align-items-center"><span class="pe-2">${iconPh(varName)}</span>${esc(ucfirst(varName.replace(/_/g, ' ')))}</span>`
                + `<span class="badge ${gp < 0 ? 'bg-danger' : 'bg-warning text-dark'}">${fmt(gp)} GP (${rnd(gp / 100, 2)} PL)</span></li>`);
        }
        out.push(`<div class="accordion-item"><h2 class="accordion-header"><button class="accordion-button collapsed d-flex align-items-center" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-${esc(layerType)}">`
            + `${iconPh(layerType)}&nbsp; ${esc(words(layerType))}<span class="ms-2 badge bg-secondary">${rnd(ltGp / 100, 1)} PL</span></button></h2>`
            + `<div id="collapse-${esc(layerType)}" class="accordion-collapse collapse"><div class="accordion-body p-0"><ul class="list-unstyled mb-0">${lis.join('')}</ul></div></div></div>`);
    }
    out.push('</div>');

    if (isEncompassed && !isPureAbility) {
        out.push(`<div class="mt-4 p-3 bg-dark text-light rounded border"><strong>Final Calculation</strong><br><span class="small opacity-75">Sum of all category costs — PL = gp ÷ 100</span>`
            + `<div class="mt-3 p-3 bg-dark border border-secondary rounded text-light">${fmt(v.totalGp)} gp = <strong>${fmt(v.pl, 2)} PL</strong></div></div>`);
    }
    if (v.fullPageLink) {
        out.push(`<div class="mt-3 text-center"><a href="${esc(v.baseUrl)}options/pl_breakdown_full.php?item_id=${encodeURIComponent(v.id)}" class="btn btn-sm btn-outline-primary" target="_blank">Open Full Page View →</a></div>`);
    }
    return out.join('');
}
